import { useState, useCallback, useRef, useEffect } from "react";
import { getSupplierDetailByCode } from "@/apis/supplier";

// ==================== 类型定义 ====================
export interface SupplierSelectOption {
  label: string;
  value: string;
}

export interface SupplierDataItem {
  id?: string | number;
  materialCategoryName: string;
  materialCategoryValue: string;
  materialTwoCategoryName: string;
  materialTwoCategoryValue: string;
  supplierUserCode: string;
  supplierUserName: string;
  supplierUsUserName: string;
}

// ==================== 自定义Hook ====================
/**
 * 供应商数据查询Hook
 * 用于处理供应商编号下拉框的模糊搜索、数据加载及自动填充
 *
 * 特点：
 * 1. 支持通过供应商编号进行模糊搜索
 * 2. 处理异步数据加载和错误处理
 * 3. 提供搜索回调函数供Select组件使用
 * 4. 保存完整的供应商数据（包含供应商名称、物料类别等）用于自动填充表单
 * 5. 提供 getSupplierInfo 方法用于获取供应商详细信息
 */
export const useSupplierData = () => {
  const [supplierOptions, setSupplierOptions] = useState<
    SupplierSelectOption[]
  >([]);
  const [supplierDataMap, setSupplierDataMap] = useState<
    Record<string, SupplierDataItem>
  >({});
  const [loading, setLoading] = useState(false);

  // 使用ref防止重复请求
  const timeoutRef = useRef<NodeJS.Timeout>();

  // 查询供应商数据 - 稳定的函数引用
  const fetchSupplierData = useCallback(async (supplierCode: string = "") => {
    // 清空之前的超时
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 空值处理
    if (!supplierCode || supplierCode.trim() === "") {
      setSupplierOptions([]);
      setSupplierDataMap({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // 添加防抖延迟，避免输入时频繁请求
      timeoutRef.current = setTimeout(async () => {
        try {
          const response = await getSupplierDetailByCode(supplierCode.trim());

          console.log("供应商查询响应:", response);

          const supplierDataList = response?.data?.data;

          // 检查是否返回的是数组
          if (Array.isArray(supplierDataList) && supplierDataList.length > 0) {
            const options: SupplierSelectOption[] = supplierDataList.map(
              (item) => ({
                label: `${item.supplierUserCode}`,
                value: item.supplierUserCode,
              }),
            );
            setSupplierOptions(options);

            // 将供应商数据存储在map中，便于通过供应商编号快速获取
            const dataMap: Record<string, SupplierDataItem> = {};
            supplierDataList.forEach((item) => {
              dataMap[item.supplierUserCode] = item;
            });
            setSupplierDataMap(dataMap);
          } else {
            console.warn("未找到匹配的供应商:", supplierCode);
            setSupplierOptions([]);
            setSupplierDataMap({});
          }
        } catch (error) {
          setSupplierOptions([]);
          setSupplierDataMap({});
        } finally {
          setLoading(false);
        }
      }, 500);
    } catch (error) {
      console.error("查询供应商数据异常:", error);
      setLoading(false);
    }
  }, []);

  // 处理搜索变化 - 直接调用 fetchSupplierData
  const handleSupplierSearch = useCallback(
    (searchValue: string) => {
      console.log("搜索供应商编号:", searchValue);
      fetchSupplierData(searchValue);
    },
    [fetchSupplierData],
  );

  // 根据供应商编号获取供应商详细信息
  const getSupplierInfo = useCallback(
    (supplierUserCode: string) => {
      return supplierDataMap[supplierUserCode] || null;
    },
    [supplierDataMap],
  );

  // 清理超时
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    supplierOptions,
    loading,
    fetchSupplierData,
    handleSupplierSearch,
    getSupplierInfo,
    supplierDataMap,
  };
};
